import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import ChangePassword from './components/ChangePassword';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import SetPassword from './components/SetPassword';
import ConferenceManagement from './components/ConferenceManagement';
import Analytics from './components/Analytics';
import MasterData from './components/MasterData';
import RegistrationList from './components/RegistrationList';
import FeeManagement from './components/FeeManagement';
import TrackManagement from './components/TrackManagement';
import ReviewerManagement from './components/ReviewerManagement';
import AbstractMetadataManagement from './components/AbstractMetadataManagement';
import MyReviews from './components/MyReviews';
import PaperManagement from './components/PaperManagement';
import PaperAssignment from './components/PaperAssignment';
import ReviewManagement from './components/ReviewManagement';
import ReferralManagement from './components/ReferralManagement';
import ChairManagement from './components/ChairManagement';
import ConferenceGroupManagement from './components/ConferenceGroupManagement';
import ConferenceGroupLoginManagement from './components/ConferenceGroupLoginManagement';

// Protected Route Component
function ProtectedRoute({ children }) {
  const adminUser = localStorage.getItem('adminUser');
  
  if (!adminUser) {
    return <Navigate to="/login" replace />;
  }
  
  const user = JSON.parse(adminUser);
  if (user.firstLogin) {
    return <Navigate to="/change-password" replace />;
  }
  
  return children;
}

// Admin Layout Component
function AdminLayout() {
  const [activeMenu, setActiveMenu] = useState('analytics');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Set active menu based on current path
    const path = location.pathname.substring(1);
    if (path) {
      setActiveMenu(path);
    }
  }, [location]);

  const handleMenuClick = (menu) => {
    setActiveMenu(menu);
    navigate(`/${menu}`);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminUser');
    navigate('/login');
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'conference':
        return <ConferenceManagement />;
      case 'registrations':
        return <RegistrationList />;
      case 'analytics':
        return <Analytics />;
      case 'master-data':
        return <MasterData />;
      case 'fee-management':
        return <FeeManagement />;
      case 'tracks':
        return <TrackManagement />;
      case 'reviewers':
        return <ReviewerManagement />;
      case 'abstract-metadata':
        return <AbstractMetadataManagement />;
      case 'my-reviews':
        return <MyReviews />;
      case 'paper-management':
        return <PaperManagement />;
      case 'paper-assignment':
        return <PaperAssignment />;
      case 'review-management':
        return <ReviewManagement />;
      case 'referral-management':
        return <ReferralManagement />;
      case 'chair-management':
        return <ChairManagement />;
      case 'conference-groups':
        return <ConferenceGroupManagement />;
      case 'conference-group-logins':
        return <ConferenceGroupLoginManagement />;
      default:
        return <Analytics />;
    }
  };

  const user = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isAdmin = user.role === 'ADMIN';
  const isChair = user.role === 'CHAIR';
  const isReviewer = user.role === 'REVIEWER';

  return (
    <div className="admin-portal">
      <div className="sidebar">
        <div className="sidebar-header">
          <h2>Admin Portal</h2>
          <p style={{ fontSize: '12px', color: '#95a5a6', marginTop: '5px' }}>
            {user.fullName || `${user.firstName} ${user.lastName}`}
          </p>
          <p style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '2px' }}>
            {user.role}
          </p>
        </div>
        <ul className="sidebar-menu">
          {/* Reviewer-specific menu */}
          {isReviewer && (
            <li
              className={`menu-item ${activeMenu === 'my-reviews' ? 'active' : ''}`}
              onClick={() => handleMenuClick('my-reviews')}
            >
              <i>📄</i>
              <span>My Reviews</span>
            </li>
          )}

          {/* Chair-specific menus */}
          {isChair && (
            <>
              <li
                className={`menu-item ${activeMenu === 'reviewers' ? 'active' : ''}`}
                onClick={() => handleMenuClick('reviewers')}
              >
                <i>👨‍🏫</i>
                <span>Reviewers</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'referral-management' ? 'active' : ''}`}
                onClick={() => handleMenuClick('referral-management')}
              >
                <i>🔔</i>
                <span>Referral Management</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'paper-assignment' ? 'active' : ''}`}
                onClick={() => handleMenuClick('paper-assignment')}
              >
                <i>📄</i>
                <span>Paper Assignment</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'paper-management' ? 'active' : ''}`}
                onClick={() => handleMenuClick('paper-management')}
              >
                <i>📝</i>
                <span>Paper Management</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'review-management' ? 'active' : ''}`}
                onClick={() => handleMenuClick('review-management')}
              >
                <i>📋</i>
                <span>Review Management</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'my-reviews' ? 'active' : ''}`}
                onClick={() => handleMenuClick('my-reviews')}
              >
                <i>📄</i>
                <span>My Reviews</span>
              </li>
            </>
          )}

          {/* Admin-specific menus */}
          {isAdmin && (
            <>
              <li
                className={`menu-item ${activeMenu === 'analytics' ? 'active' : ''}`}
                onClick={() => handleMenuClick('analytics')}
              >
                <i>📊</i>
                <span>Analytics</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'conference' ? 'active' : ''}`}
                onClick={() => handleMenuClick('conference')}
              >
                <i>📋</i>
                <span>Conference</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'registrations' ? 'active' : ''}`}
                onClick={() => handleMenuClick('registrations')}
              >
                <i>👥</i>
                <span>Registrations</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'master-data' ? 'active' : ''}`}
                onClick={() => handleMenuClick('master-data')}
              >
                <i>⚙️</i>
                <span>Master Data</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'fee-management' ? 'active' : ''}`}
                onClick={() => handleMenuClick('fee-management')}
              >
                <i>💰</i>
                <span>Fee Management</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'tracks' ? 'active' : ''}`}
                onClick={() => handleMenuClick('tracks')}
              >
                <i>🎯</i>
                <span>Tracks</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'chair-management' ? 'active' : ''}`}
                onClick={() => handleMenuClick('chair-management')}
              >
                <i>👔</i>
                <span>Chairperson Management</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'abstract-metadata' ? 'active' : ''}`}
                onClick={() => handleMenuClick('abstract-metadata')}
              >
                <i>📝</i>
                <span>Abstract Metadata</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'conference-groups' ? 'active' : ''}`}
                onClick={() => handleMenuClick('conference-groups')}
              >
                <i>🏢</i>
                <span>Conference Groups</span>
              </li>
              <li
                className={`menu-item ${activeMenu === 'conference-group-logins' ? 'active' : ''}`}
                onClick={() => handleMenuClick('conference-group-logins')}
              >
                <i>🔐</i>
                <span>Group Logins</span>
              </li>
            </>
          )}
        </ul>
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </div>
      <div className="main-content">
        {renderContent()}
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route 
          path="/analytics" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/conference" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/registrations" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/master-data" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/fee-management" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tracks" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/reviewers" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/abstract-metadata" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/my-reviews" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/paper-management" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/paper-assignment" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/review-management" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/referral-management" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/chair-management" 
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/conference-groups"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/conference-group-logins"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
